import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/globals/terms-of-service`.
 *
 * Same 3-phase versioning as tasks/adventures/cohorts/sweepstakes.
 * ToS adds:
 * - `tos_body` — long-form legal text (TEXT / large)
 * - `language_code` — localized
 * - `version_number` — human-facing legal version (distinct from the
 *   platform's `current_version` which is the edit-history version)
 * - `effective_date` — when this version goes live contractually
 */

export interface ToSHistoryRow {
  version: number;
  tos_name: string | null;
  language_code: string;
  tos_body: string | null;
  version_number: string | null;
  effective_date: string | null;
  platform_tos_id: string | number | null;
  active: boolean;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'delete' | 'publish' | 'rollback';
  created_at: string;
  created_by: string | null;
}

export interface AdminToSRow {
  tos_code: string;
  tos_name: string | null;
  language_code: string;
  tos_body: string | null;
  version_number: string | null;
  effective_date: string | null;
  platform_tos_id: string | number | null;
  active: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
  status: 'in_sync' | 'never_published' | 'unpublished';
}

export interface AdminToSDetail extends AdminToSRow {
  history: ToSHistoryRow[];
}

export interface ListToSResponse {
  rows: AdminToSRow[];
}

export interface ListToSParams {
  sort?: 'tos_code' | 'tos_name' | 'updated_at' | 'effective_date' | 'current_version';
  dir?: 'asc' | 'desc';
  status?: 'in_sync' | 'never_published';
  language_code?: string;
}

export interface CreateToSPayload {
  tos_code: string;
  tos_name: string;
  language_code: string;
  tos_body?: string | null;
  version_number?: string | null;
  effective_date?: string | null;
  change_summary?: string;
}

export interface UpdateToSPayload {
  base_version: number;
  tos_name?: string;
  language_code?: string;
  tos_body?: string | null;
  version_number?: string | null;
  effective_date?: string | null;
  active?: boolean;
  change_summary?: string;
}

export interface PublishToSEntityRequest {
  tos_code: string;
  base_version: number;
}

export interface PublishToSRequest {
  entities: PublishToSEntityRequest[];
  batch_id?: string;
  change_summary?: string;
}

export interface PublishToSEntityResult {
  tos_code: string;
  ok: boolean;
  published_version?: number;
  error?: string;
  message?: string;
  current_version?: number;
}

export interface PublishToSResponse {
  batch_id: string;
  results: PublishToSEntityResult[];
}

export interface RollbackToSPayload {
  target_version: number;
  base_version: number;
  change_summary?: string;
}

export const adminToSApi = {
  list: async (params: ListToSParams = {}): Promise<AdminToSRow[]> => {
    const res = await axiosClient.get<ListToSResponse>(
      '/api/admin/globals/terms-of-service',
      { params },
    );
    return res.data.rows;
  },

  get: async (code: string): Promise<AdminToSDetail> => {
    const res = await axiosClient.get<AdminToSDetail>(
      `/api/admin/globals/terms-of-service/${encodeURIComponent(code)}`,
    );
    return res.data;
  },

  create: async (payload: CreateToSPayload): Promise<AdminToSRow> => {
    const res = await axiosClient.post<AdminToSRow>(
      '/api/admin/globals/terms-of-service',
      payload,
    );
    return res.data;
  },

  update: async (
    code: string,
    payload: UpdateToSPayload,
  ): Promise<AdminToSRow> => {
    const res = await axiosClient.patch<AdminToSRow>(
      `/api/admin/globals/terms-of-service/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  publish: async (payload: PublishToSRequest): Promise<PublishToSResponse> => {
    const res = await axiosClient.post<PublishToSResponse>(
      '/api/admin/globals/terms-of-service/publish',
      payload,
    );
    return res.data;
  },

  publishOne: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string },
  ): Promise<PublishToSResponse> => {
    return adminToSApi.publish({
      entities: [{ tos_code: code, base_version }],
      batch_id: options?.batch_id,
      change_summary: options?.change_summary,
    });
  },

  rollback: async (
    code: string,
    payload: RollbackToSPayload,
  ): Promise<AdminToSRow> => {
    const res = await axiosClient.post<AdminToSRow>(
      `/api/admin/globals/terms-of-service/${encodeURIComponent(code)}/rollback`,
      payload,
    );
    return res.data;
  },
};
